import React, {useEffect, useRef, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {genreDropdownList, getFullAssetUrl} from "utils";
import {useUpdateAssetsQuery} from "hooks";
import ProgressBox from "components/Canvas/ProgressBox";
import TagClose from "images/library/tag_close.svg";
import SelectArrow from "images/library/custom_select_down.svg";
import thumbnailInputImg from "images/library/input_thumbnail_button.png";
import styles from "./index.module.scss";
import {ASSET_TYPE} from "components/Layout/constants";

function AssetPropertiesPopup({dismiss, asset}) {
  const intl = useIntl();
  const thumbnailRef = useRef();
  const [, setThumbnailFile] = useState();
  const [isPublic] = useState(asset.publicStatus);
  const [genre, setGenre] = useState(asset.genre);
  const [name, setName] = useState(asset.name);
  const [keywordList, setKeywordList] = useState([]);
  const [thumbnailPreview, setThumbnailPreview] = useState(asset.thumbnailUrl ? getFullAssetUrl(asset.thumbnailUrl) : thumbnailInputImg);
  const [isSelectingGenre, setIsSelectingGenre] = useState(false);
  const [keyword, setKeyword] = useState("");
  const keywordInputRef = useRef();
  const [uploadProgress] = useState(0);

  const isValid = asset.assetType === ASSET_TYPE.MODEL ? name && genre && keywordList : name && keywordList;
  const updateMutation = useUpdateAssetsQuery(asset.id);

  const onKeydownKeyword = e => {
    if ((e.target.value.length !== 0 && e.key === " ") || e.key === "Enter") {
      if (keywordList.length <= 10) {
        createKeyword(e);
      }
    }
    if (e.target.value.length === 0 && e.key === "Backspace") {
      onKeyDownDeleteKeyword(e);
    }
  };

  const createKeyword = e => {
    if (keywordList.includes(keyword)) {
      const filteredList = keywordList.filter(keywordItem => {
        return keywordItem !== keyword;
      });
      filteredList.push(keyword);
      setKeywordList(filteredList);
    } else {
      const updatedKeywordList = [...keywordList];
      updatedKeywordList.push(keyword);
      setKeywordList(updatedKeywordList);
    }
    setKeyword("");
  };

  const onClickDeleteKeyword = selectedKeyword => {
    const filteredList = keywordList.filter(keywordItem => {
      return keywordItem !== selectedKeyword;
    });
    setKeywordList(filteredList);
  };

  const onKeyDownDeleteKeyword = () => {
    const updatedKeywordList = [...keywordList];
    updatedKeywordList.pop();
    setKeywordList(updatedKeywordList);
  };

  const focusKeywordInput = () => {
    keywordInputRef.current.focus();
  };

  const handleGenreDropdown = () => {
    setIsSelectingGenre(!isSelectingGenre);
  };

  const onClickUpload = () => {
    let assetInfo = {
      name,
    };

    if (genre) assetInfo.genre = genre;
    if (keywordList) assetInfo.keyword = keywordList;

    try {
      updateMutation.mutateAsync(assetInfo);
    } catch (e) {}

    dismiss();
  };

  useEffect(() => {
    if (asset.assetKeywords.length) {
      const kewords = [];
      asset.assetKeywords.forEach(keyword => {
        kewords.push(keyword.name);
      });
      setKeywordList(kewords);
    }
  }, []);

  return (
    <>
      <div className={styles.asset_properties}>
        <h3 className={styles.title}>
          <FormattedMessage id="ID_ASSET_PROPERTIES_POPUP_TITLE" />
        </h3>
        <div className={styles.inner}>
          {asset.assetType === ASSET_TYPE.MODEL && (
            <>
              <input
                id="asset_properties_thumbnail_input"
                className={styles.asset_thumbnail_input}
                ref={thumbnailRef}
                type="file"
                onChange={e => {
                  setThumbnailPreview(window.URL.createObjectURL(e.target.files[0]));
                  setThumbnailFile(e.target.files[0]);
                }}
              />
              <img
                src={thumbnailPreview || thumbnailInputImg}
                className={styles.thumbnail_input_button}
                onClick={() => thumbnailRef.current.click()}
                alt="thumbnailPreviewImage"
              />
            </>
          )}

          <div className={styles.info_wrap}>
            <div className={styles.flex_row}>
              <span className={styles.category}>
                <FormattedMessage id="ID_CATEGORY_NAME" />
              </span>
              <input
                type="text"
                className={`${styles.user_input} ${styles.common_bg}`}
                maxLength="25"
                defaultValue={name}
                placeholder={intl.formatMessage({
                  id: "ID_ASSET_NAME_INPUT_PLACEHOLDER",
                })}
                onChange={e => setName(e.target.value)}
              />
              <p className={styles.count_length}>{`${name ? name.length : 0}/25`}</p>
            </div>

            {asset.assetType === ASSET_TYPE.MODEL && (
              <div className={styles.flex_row}>
                <span className={styles.category}>
                  <FormattedMessage id="ID_CATEGORY_GENRE" />
                </span>

                <div className={`${styles.custom_select} ${styles.common_bg}`}>
                  <span className={styles.select_title} onClick={handleGenreDropdown}>
                    {genre ? genre : "장르를 선택해주세요"}
                    <img alt="down arrow" src={SelectArrow} />
                  </span>

                  <div className={`${styles.drop_down} ${isSelectingGenre ? `${styles.block}` : ""}`}>
                    {genreDropdownList.map(item => (
                      <span
                        key={item.id}
                        className={styles.select_item}
                        onClick={() => {
                          setGenre(item.genre);
                          setIsSelectingGenre(false);
                        }}
                      >
                        <FormattedMessage id={item.title} />
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className={styles.flex_row}>
              <span className={styles.category}>
                <FormattedMessage id="ID_CATEGORY_KEYWORD" />
              </span>

              <div className={styles.keyword_input_container}>
                <div
                  placeholder={intl.formatMessage({
                    id: "ID_KEYWORD_PLACEHOLDER",
                  })}
                  className={`${styles.user_text} ${styles.common_bg}`}
                  onClick={focusKeywordInput}
                >
                  {keywordList?.map((keyword, index) => (
                    <span className={styles.tag} key={index}>
                      {keyword}
                      <img alt="close" src={TagClose} onClick={() => onClickDeleteKeyword(keyword)} />
                    </span>
                  ))}
                  <input
                    type="text"
                    value={keyword}
                    maxLength="8"
                    className={styles.tag_input}
                    tabIndex={2}
                    onChange={e => setKeyword(e.target.value)}
                    onKeyDown={onKeydownKeyword}
                    ref={keywordInputRef}
                  />
                </div>
                <p className={styles.keyword_limit_notice}>{keywordList?.length > 10 ? "keyword는 10개까지 등록 가능합니다." : ""}</p>
              </div>
            </div>

            <div className={styles.flex_box}>
              <span className={styles.category}>
                <FormattedMessage id="ID_CATEGORY_PUBLIC" />
              </span>
              <span className={`${styles.toggle_btn} ${isPublic ? `${styles.active}` : ""}`}>
                <span className={styles.toggle} />
              </span>
            </div>
          </div>
        </div>

        <div className={styles.buttons}>
          <button className={styles.cancel} type="button" onClick={dismiss}>
            <FormattedMessage id="ID_ACTION_CANCEL" />
          </button>
          <button className={`${styles.save} ${isValid ? styles.active : ""}`} type="button" onClick={onClickUpload} disabled={!isValid}>
            <FormattedMessage id="ID_ACTION_SAVE" />
          </button>
        </div>
      </div>
      <ProgressBox percentage={uploadProgress} loaded={!uploadProgress} />
    </>
  );
}

export default AssetPropertiesPopup;
